import moment from 'moment';
import { getMonthNumberFromName, getMonthNameFromDate } from 'common/utils';
import { shortenFormat } from 'common/formatters';
import { valuesPolyfill } from 'common/polyFills';
import { base64url_encode } from 'util/base64';
const ObjectValues = Object.values || valuesPolyfill;

/**
 *
 *
 * @function aggregateRevenueChartData
 * @param {Object} - response from graphql
 * @returns {Array} - data from graphql request
 */
export const aggregateRevenueChartData = data => {
  const chartData = {
    contracted: [],
    surplus: [],
    deficit: [],
  };

  (data || []).forEach(item => {
    const { contractedRevenue, lostRevenue, uncontractedRevenue } = item;
    const contracted = contractedRevenue;
    const surplus = uncontractedRevenue;
    const deficit = lostRevenue;
    const cropName = generateCropVarietyKey(item);

    chartData.contracted.push({ label: cropName, value: contracted });
    chartData.surplus.push({ label: cropName, value: surplus });
    chartData.deficit.push({ label: cropName, value: deficit });
  });

  return chartData;
};

/**
 *
 *
 * @function aggregateCropChartData
 * @param {Object} - response from graphql
 * @returns {Array} - data structured for the for pie chart
 */
export const aggregateCropChartData = data => {
  if (!data || !data.length) return [];
  const cropsByNameAndVariety = ObjectValues(_groupCropsByNameAndVariety(data));
  return cropsByNameAndVariety.map(
    ({ crop: { name, variety }, plantedArea: { size, type } }) => {
      return {
        x: `${name}\n${variety}`,
        y: size,
        unit: type,
      };
    }
  );
};

/**
 *
 *
 * @function aggregateYieldHistoryChartData
 * @param {Object} - response from graphql
 * @returns {Array} chartData - data structured for the yield history chart
 */
export const aggregateYieldHistoryChartData = data => {
  if (!data || !data.length) return {};

  const groupedByCropWithLatestDiaries = _groupDiariesByField(data);

  // Return line chart data
  return _getLineChartData(groupedByCropWithLatestDiaries);
};

/**
 *
 *
 * @function _groupDiariesByField
 * @param {Object} data - response from graphql
 * @returns {Object}
 */
export const _groupDiariesByField = data => {
  const latestYieldsByMonthFromAllFields = data.map(field => {
    const cropId = field.id;
    const cropName = generateCropVarietyKey(field);
    const expectedYieldArray = field.expectedYield;
    const plantedAreaSize = field.plantedArea.size;

    // For each diary entry in each field get only the latest
    return expectedYieldArray.reduce((acc, currentYieldObj) => {
      const currentTimeStamp = moment.utc(currentYieldObj.timestamp);
      const monthName = getMonthNameFromDate(currentYieldObj.timestamp);

      // console.log(currentYieldObj)

      acc[monthName] = acc[monthName] || currentYieldObj;

      acc[monthName] = {
        ...acc[monthName],
        timestamp: acc[monthName].timestamp,
        monthName: monthName,
        plantedAreaSize,
        cropName,
        cropId,
        fieldName: field.fieldName,
        season: moment(acc[monthName].timestamp).year(),
      };

      // If the current diary entry is "later" than current saved diary, replace it
      if (
        currentTimeStamp.isSameOrAfter(
          moment.utc(acc[monthName].timestamp),
          'second'
        )
      ) {
        acc[monthName] = {
          ...currentYieldObj,
          timestamp: currentYieldObj.timestamp,
          monthName: monthName,
          plantedAreaSize,
          cropName,
          cropId,
          fieldName: field.fieldName,
          season: moment(currentYieldObj.timestamp).year(),
        };
      }

      return acc;
    }, {});
  });

  let resultObject = [];
  const filteredEntries = {};

  /**
   * Returns the object that are used in YieldChart container
   */
  const getUnitObj = ({
    cropId,
    yield: { size, harvested },
    plantedAreaSize,
    season,
  }) => ({
    cropId: base64url_encode(cropId),
    yield: size,
    harvest: plantedAreaSize * size,
    harvested,
    season,
  });

  latestYieldsByMonthFromAllFields.forEach(cropObject => {
    const latestDirariesPerField = ObjectValues(cropObject);
    resultObject = resultObject.concat(latestDirariesPerField);
  });

  resultObject.forEach(item => {
    const sameMonthAndCrop = resultObject.filter(
      ({ cropName, monthName }) =>
        item.cropName === cropName && item.monthName === monthName
    );

    let totalUnits = {};
    const filteredByTime = sameMonthAndCrop.reduce((acc, curr, i) => {
      const currentTimeStamp = moment.utc(curr.timestamp);
      const accumulatorTimeStamp = moment.utc(acc.timestamp);
      const isAfter = accumulatorTimeStamp.isSameOrAfter(
        currentTimeStamp,
        'second'
      );
      acc = Object.keys(acc).length ? acc : curr;
      const result = isAfter && i ? acc : curr;

      /**
       * Put each [yield] * [planted area] by fieldName
       */
      totalUnits[base64url_encode(acc.fieldName)] = getUnitObj(acc);
      totalUnits[base64url_encode(curr.fieldName)] = getUnitObj(curr);

      return { ...result, totalUnits };
    });

    filteredEntries[item.cropName] = filteredEntries[item.cropName] || {};
    filteredEntries[item.cropName][filteredByTime.monthName] = filteredByTime;
  });

  return ObjectValues(filteredEntries).reduce((result, latestPerMonthEntry) => {
    const values = ObjectValues(latestPerMonthEntry);
    values.forEach(dataPoint => {
      const cropName = dataPoint.cropName;
      const monthName = dataPoint.monthName;
      result[cropName] = result[cropName] || {};
      result[cropName][monthName] = result[cropName][monthName] || {
        ...dataPoint,
      };

      if (!result[cropName][monthName].totalUnits) {
        let totalUnits = {};
        /**
         * Put [yield] * [planted area] by fieldName
         */
        totalUnits[base64url_encode(dataPoint.fieldName)] = getUnitObj(
          latestPerMonthEntry[monthName]
        );
        result[cropName][monthName].totalUnits = totalUnits;
      }
    });

    return result;
  }, {});
};

/**
 *
 *
 * @function _getLineChartData
 * @param {Object} - cropsPerFieldWithLatestEntries
 * @returns {Object} - chartData
 */
const _getLineChartData = cropsWithTotalUnits => {
  const chartData = {};
  const selectOptions = [];
  const cropValues = ObjectValues(cropsWithTotalUnits);

  Object.keys(cropsWithTotalUnits).forEach(cropName => {
    selectOptions.push({
      label: shortenFormat(cropName, 20),
      value: cropName,
    });
  });

  const dataPoints = {};

  // Create final chartData object based on cropVarietyKeys
  cropValues.forEach(dataPointInfoObjectsKeyedByMonth => {
    ObjectValues(dataPointInfoObjectsKeyedByMonth).forEach(dataPointInfo => {
      const cropName = dataPointInfo.cropName;
      const monthName = dataPointInfo.monthName;
      const totalUnits = dataPointInfo.totalUnits;

      // Create a data point and add it to our return object (chartData)
      dataPoints[cropName] = dataPoints[cropName] || [];

      dataPoints[cropName].push({ x: monthName, y: totalUnits });

      chartData[cropName] = chartData[cropName] || [];
      chartData[cropName] = { dataPoints: dataPoints[cropName] };

      // Sort data points according to month number
      dataPoints[cropName].sort((a, b) => {
        const aMonthNumber = getMonthNumberFromName(a.x);
        const bMonthNumber = getMonthNumberFromName(b.x);
        return aMonthNumber - bMonthNumber;
      });
    });
  });

  // Add select options to chartData object
  chartData.selectOptions = selectOptions;
  return chartData;
};

/**
 *
 *
 * @function _groupCropsByNameAndVariety
 * @param {Object} data - response from graphql
 * @returns {Array}
 */
const _groupCropsByNameAndVariety = data =>
  data &&
  [].concat(data).reduce((result, item) => {
    const {
      crop: { name, variety },
      plantedArea: { size, type },
    } = item;
    const key = generateCropVarietyKey(item);
    result[key] = result[key] || {};
    result[key].plantedArea = result[key].plantedArea || {};
    result[key] = {
      crop: { name, variety: variety || '' },
      plantedArea: { size: (result[key].plantedArea.size || 0) + size, type },
    };
    return result;
  }, []);

/**
 *
 *
 * @function generateCropVarietyKey
 * @param {Object} - crop object
 * @returns {Object}
 */
const generateCropVarietyKey = ({ crop: { name, variety } }) =>
  `${name} ${variety || ''}`;



// WEBPACK FOOTER //
// ./src/hoc/dashboard/aggregators.js